import { useCallback, useEffect, useRef, useState } from 'react';
import { sameContent } from './normalize.ts';

export interface EditorState<T> {
  /** What the form shows; `undefined` until the server value arrives. */
  draft: T | undefined;
  setDraft: (next: T) => void;
  update: (patch: Partial<T>) => void;
  /** A real change against the last saved value (see `normalize`). */
  isDirty: boolean;
  /** Back to the last saved value. */
  reset: () => void;
  /** After a save: the saved value becomes the baseline. */
  markSaved: (saved: T) => void;
}

/**
 * Form state of an editor page (BB-20). The draft follows the server value until the person edits; a newer server
 * value (refetch) never overwrites their edits. `compare` maps a value to what counts as content (e.g. without derived
 * fields).
 */
export function useEditorState<T>(server: T | undefined, compare: (value: T) => unknown = (v) => v): EditorState<T> {
  const [baseline, setBaseline] = useState<T | undefined>(server);
  const [draft, setDraftState] = useState<T | undefined>(server);
  const edited = useRef(false);
  useEffect(() => {
    if (server === undefined || edited.current) return;
    setBaseline(server);
    setDraftState(server);
  }, [server]);
  const isDirty = draft !== undefined && baseline !== undefined && !sameContent(compare(draft), compare(baseline));
  const setDraft = useCallback((next: T) => {
    edited.current = true;
    setDraftState(next);
  }, []);
  const update = useCallback((patch: Partial<T>) => {
    edited.current = true;
    setDraftState((current) => (current === undefined ? current : { ...current, ...patch }));
  }, []);
  const reset = useCallback(() => {
    edited.current = false;
    setDraftState(baseline);
  }, [baseline]);
  const markSaved = useCallback((saved: T) => {
    edited.current = false;
    setBaseline(saved);
    setDraftState(saved);
  }, []);
  return { draft, setDraft, update, isDirty, reset, markSaved };
}
